import React from 'react';
import { FormProvider } from 'react-hook-form';
import { Block as BlockIcon } from '@mui/icons-material';
import MailOutlineIcon from '@mui/icons-material/MailOutlined';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import { TablePaginationControls } from '~/components/TablePaginationControls/TablePaginationControls.tsx';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import { managerStyles } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/styles.ts';
import BlockedEmailRow from '~/modules/covenants/CovenantsEmailBlackList/BlockedEmailRow.tsx';
import {
  BLACK_LIST_ACTIONS_COLUMN_LABEL,
  BLACK_LIST_ADD_EMAIL_BUTTON_LABEL,
  BLACK_LIST_ADD_EMAIL_HINT,
  BLACK_LIST_ADD_EMAIL_LOADING_LABEL,
  BLACK_LIST_ADD_EMAIL_LOADING_TOOLTIP,
  BLACK_LIST_ADD_EMAIL_TITLE,
  BLACK_LIST_ADD_EMAIL_TOOLTIP,
  BLACK_LIST_BLOCKED_AT_COLUMN_LABEL,
  BLACK_LIST_EMAIL_COLUMN_LABEL,
  BLACK_LIST_EMPTY_STATE,
  BLACK_LIST_ROWS_PER_PAGE_LABEL,
  BLACK_LIST_TABLE_TITLE,
} from '~/modules/covenants/CovenantsEmailBlackList/constants.ts';
import useSupervisorContragentBlackListForm from '~/modules/covenants/CovenantsEmailBlackList/hooks/useSupervisorContragentBlackListForm.ts';
import { blackListStyles } from '~/modules/covenants/CovenantsEmailBlackList/styles.ts';
import type {
  TSupervisorContragentBlackListCardProps,
  TSupervisorContragentBlackListFormValues,
} from '~/modules/covenants/CovenantsEmailBlackList/types.ts';

const SupervisorContragentBlackListCard: React.FC<TSupervisorContragentBlackListCardProps> = ({
  contragent,
  blocks,
  page,
  rowsPerPage,
  pageConfiguration,
  isLoading,
  isSubmittingAdd = false,
  deletingEmail = null,
  onAddEmail,
  onDeleteEmail,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const formMethods = useSupervisorContragentBlackListForm();
  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = formMethods;

  const handleAddSubmit = async (values: TSupervisorContragentBlackListFormValues) => {
    await onAddEmail({
      email: values.email.trim(),
    });
    reset({ email: '' });
  };

  const isAddDisabled = isSubmitting || isSubmittingAdd;
  const addButtonTooltipTitle = isAddDisabled
    ? BLACK_LIST_ADD_EMAIL_LOADING_TOOLTIP
    : BLACK_LIST_ADD_EMAIL_TOOLTIP;

  return (
    <Paper elevation={0} sx={managerStyles.tablePaper}>
      <Box sx={managerStyles.tableToolbar}>
        <Box sx={managerStyles.tableToolbarContent}>
          <BlockIcon color="secondary" />
          <Typography variant="h6" sx={managerStyles.tableTitle}>
            {BLACK_LIST_TABLE_TITLE}
          </Typography>
        </Box>
      </Box>

      <FormProvider {...formMethods}>
        <Box
          component="form"
          onSubmit={handleSubmit(handleAddSubmit)}
          sx={blackListStyles.formSection}
        >
          <Box sx={blackListStyles.formHeader}>
            <MailOutlineIcon color="action" fontSize="small" />
            <Typography variant="subtitle1" sx={blackListStyles.formTitle}>
              {BLACK_LIST_ADD_EMAIL_TITLE}
            </Typography>
          </Box>

          <FormInput
            name="email"
            label="Email"
            placeholder="user@example.com"
            type="email"
            fullWidth
            disabled={isAddDisabled}
            autoComplete={`section-${contragent.id} email`}
            sx={blackListStyles.formField}
          />

          <Box sx={blackListStyles.formActions}>
            <Typography variant="body2" sx={blackListStyles.formHint}>
              {BLACK_LIST_ADD_EMAIL_HINT}
            </Typography>

            <Tooltip title={addButtonTooltipTitle} arrow>
              <Box sx={blackListStyles.submitButtonWrap}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isAddDisabled}
                  startIcon={
                    isAddDisabled ? <CircularProgress size={18} color="inherit" /> : undefined
                  }
                  sx={blackListStyles.submitButton}
                >
                  {isAddDisabled
                    ? BLACK_LIST_ADD_EMAIL_LOADING_LABEL
                    : BLACK_LIST_ADD_EMAIL_BUTTON_LABEL}
                </Button>
              </Box>
            </Tooltip>
          </Box>
        </Box>
      </FormProvider>

      <Divider />

      <Box sx={commonStyles.loadingWrap}>
        <TableContainer sx={managerStyles.tableContainer}>
          <Table sx={managerStyles.tableRoot}>
            <TableHead sx={managerStyles.tableHead}>
              <TableRow sx={managerStyles.tableHeaderRow}>
                <TableCell sx={managerStyles.tableHeaderCell}>
                  {BLACK_LIST_EMAIL_COLUMN_LABEL}
                </TableCell>
                <TableCell align="center" sx={managerStyles.tableHeaderCell}>
                  {BLACK_LIST_BLOCKED_AT_COLUMN_LABEL}
                </TableCell>
                <TableCell align="right" sx={managerStyles.tableHeaderCell}>
                  {BLACK_LIST_ACTIONS_COLUMN_LABEL}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!blocks.length ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={blackListStyles.emptyCell}>
                    {BLACK_LIST_EMPTY_STATE}
                  </TableCell>
                </TableRow>
              ) : (
                blocks.map((block) => (
                  <BlockedEmailRow
                    key={block.id}
                    block={block}
                    deletingEmail={deletingEmail}
                    onDeleteEmail={onDeleteEmail}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {isLoading && (
          <Box sx={commonStyles.loadingMask}>
            <CircularProgress size={28} />
          </Box>
        )}
      </Box>

      <Box sx={managerStyles.tablePagination}>
        <TablePaginationControls
          page={page}
          rowsPerPage={rowsPerPage}
          totalElements={pageConfiguration.totalElements}
          isLoading={isLoading}
          labelRowsPerPage={BLACK_LIST_ROWS_PER_PAGE_LABEL}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </Box>
    </Paper>
  );
};

export default SupervisorContragentBlackListCard;
